// API-key gating for the research wizards. Ports the legacy validate*Toggle logic
// (Backup/static/js/components/api-key-tester.js + the research templates): each additional-search
// Data Source toggle depends on a provider key, and a toggle is disabled + grayed when its key's
// probe didn't come back 'valid'. The probe itself is POST /profile/user-preferences/test-api-keys
// (see useApiKeyStatus).
//
// Data Source keys (Apollo / Maps / Coresignal / LinkedIn / Hunter) are user-facing.
// Language models, search providers (News / Serper / Perplexity), tearsheet, and Parallel.ai
// (FindAll) are added by Quralyst. Toggles that depend on those managed keys stay OFF with no
// API-key error.
// If processing still cannot start, the user sees PLATFORM_ADMIN_CONTACT_MESSAGE.
import type { ApiKeyTestResult, OrgKeyName } from '@/types';
import { USER_EDITABLE_KEYS } from '@/features/apiKeys/catalog';

export type TestResults = Partial<Record<OrgKeyName, ApiKeyTestResult>>;

/** Fallback if a platform-managed key (LLM / search / tearsheet / Parallel.ai) is missing at start. */
export const PLATFORM_ADMIN_CONTACT_MESSAGE =
  'Contact Platform Admin, you cannot start processing right now';

const DATA_SOURCE_ERROR_HINT = /apollo|coresignal|google maps|gmaps|apify|linkedin|hunter/i;

const ORG_MANAGED_API_ERROR_HINT =
  /api key|missing_api_keys|not configured|ask quralyst|organization-managed|language model|openai|anthropic|claude|gemini|perplexity|gamma|serper|news api|llm|parallel|findall/i;

/** Replace API-key copy for keys the user does not enter. Keep Data Source errors as-is. */
export function sanitizeOrgManagedApiError(message: string): string {
  const text = message.trim();
  if (!text) return text;
  if (DATA_SOURCE_ERROR_HINT.test(text)) return text;
  if (ORG_MANAGED_API_ERROR_HINT.test(text)) return PLATFORM_ADMIN_CONTACT_MESSAGE;
  return text;
}

export function userFacingStartError(err: unknown, fallback: string): string {
  if (typeof err === 'object' && err !== null && 'message' in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return sanitizeOrgManagedApiError(message);
    }
  }
  return fallback;
}

// Short, human label for a provider key — used in the "<X> is unavailable" copy.
const KEY_LABEL: Record<OrgKeyName, string> = {
  openai_api_key: 'OpenAI',
  anthropic_api_key: 'Anthropic',
  gemini_api_key: 'Google (Gemini)',
  apollo_api_key: 'Apollo',
  news_api_key: 'News API',
  gmaps_api_key: 'Google Maps',
  coresignal_api_key: 'Coresignal',
  apify_api_key: 'LinkedIn (Apify)',
  serper_api_key: 'Serper (Search)',
  hunter_api_key: 'Hunter',
  perplexity_api_key: 'Perplexity',
  gamma_api_key: 'Gamma',
  parallel_api_key: 'Parallel.ai',
};

function remediationFor(): string {
  return 'Add your key in Settings → API Keys, or ask your organization admin for a default.';
}

/** A key is usable only when its probe returned 'valid'. Anything else — missing, invalid, quota /
 *  credits exhausted, rate-limited, timeout, error, or simply absent from the response — disables
 *  the features that depend on it. */
export function keyOk(results: TestResults | undefined, key: OrgKeyName): boolean {
  return results?.[key]?.status === 'valid';
}

/** Why a key's toggle is disabled — shown on the toggle so a grayed switch is never a silent mystery.
 *
 * Surfaces the **backend's own message** for the key (the exact text the Settings → API Keys test
 * page shows), so both places explain the problem identically — e.g. "API key appears valid
 * (authentication successful) but API returned server errors (500)…" or "Invalid API key or
 * unauthorized". Falls back to a short pointer only when the check returned no message for this key. */
export function keyReason(results: TestResults | undefined, key: OrgKeyName): string {
  const label = KEY_LABEL[key];
  const result = results?.[key];
  switch (result?.status) {
    case 'not_configured':
      return `${label} API key is not configured. ${remediationFor()}`;
    case 'invalid':
      return `${label} API key is invalid.`;
    case 'quota_exceeded':
      return `${label} API quota is exhausted.`;
    case 'credits_exhausted':
      return `${label} API credits are exhausted.`;
    case 'rate_limited':
      return `${label} API is rate limited — try again later.`;
    case 'timeout':
      return `Couldn't reach ${label} (request timed out).`;
    default:
      return result?.message ? `${label}: ${result.message}` : `${label} API is unavailable.`;
  }
}

export interface ToggleGate {
  disabled: boolean;
  reason: string; // '' when enabled or still verifying
}

/** Compute a Data Source toggle's gate from the probe state. While verifying we leave the toggle
 *  ENABLED (the user fills details freely; only the final "start processing" action is held). Once
 *  results are in, a non-valid Data Source key disables the toggle with a reason. Quralyst-managed
 *  enrichment features use newsGate / llmGate / webSearchGate / blankFieldBackfillGate instead. */
export function gateFor(
  results: TestResults | undefined,
  key: OrgKeyName,
  verifying: boolean,
): ToggleGate {
  if (!USER_EDITABLE_KEYS.has(key)) return { disabled: false, reason: '' };
  if (verifying) return { disabled: false, reason: '' };
  if (keyOk(results, key)) return { disabled: false, reason: '' };
  return { disabled: true, reason: keyReason(results, key) };
}

// Providers that can satisfy an LLM-backed feature (ownership enrichment runs through the modular
// LLM, so it needs at least one valid provider key).
const LLM_KEYS: OrgKeyName[] = ['openai_api_key', 'anthropic_api_key', 'gemini_api_key'];

export type LlmProviderName = 'openai' | 'anthropic' | 'google';

export const LLM_PROVIDER_NAMES: LlmProviderName[] = ['openai', 'anthropic', 'google'];

/** LLM provider toggle → the API key it depends on. */
export const LLM_PROVIDER_KEY: Record<LlmProviderName, OrgKeyName> = {
  openai: 'openai_api_key',
  anthropic: 'anthropic_api_key',
  google: 'gemini_api_key',
};

export type LlmProviderSelection = Record<LlmProviderName, boolean>;

export function anyLlmValid(results: TestResults | undefined): boolean {
  return LLM_KEYS.some((k) => keyOk(results, k));
}

/** Providers whose keys probed as valid (usable for selection). */
export function validLlmProviders(results: TestResults | undefined): LlmProviderName[] {
  return LLM_PROVIDER_NAMES.filter((p) => keyOk(results, LLM_PROVIDER_KEY[p]));
}

/** True when this is the only selected provider — turning it off would leave none
 *  (OpenAI / Claude / Gemini are treated the same). */
export function isLastSelectedLlm(providers: LlmProviderSelection, name: LlmProviderName): boolean {
  if (!providers[name]) return false;
  return !LLM_PROVIDER_NAMES.some((p) => p !== name && providers[p]);
}

/** When none of the usable providers are selected, return one to force back on. */
export function providerToForceSelect(
  results: TestResults | undefined,
  providers: LlmProviderSelection,
): LlmProviderName | null {
  const usable = validLlmProviders(results);
  if (!usable.length) return null;
  if (usable.some((p) => providers[p])) return null;
  // Prefer any already-selected name if somehow selected-but-invalid slipped through,
  // otherwise the first usable provider (openai → anthropic → google).
  return usable[0] ?? null;
}

/** Keys that can run LLM-backed enrichment (ownership, acquisition, blank-field extraction). */
export const LLM_ENRICHMENT_KEYS: OrgKeyName[] = [
  'openai_api_key',
  'anthropic_api_key',
  'gemini_api_key',
  'perplexity_api_key',
];

/** Blank-field web backfill can search via Brave/Serper/Perplexity or an LLM with web search. */
export const BLANK_FIELD_KEYS: OrgKeyName[] = [
  ...LLM_ENRICHMENT_KEYS,
  'serper_api_key',
  'news_api_key',
];

/** Readiness / buyer-propensity scoring uses Gemini Search grounding, else Perplexity. */
export const READINESS_KEYS: OrgKeyName[] = ['gemini_api_key', 'perplexity_api_key'];

export function anyKeyOk(results: TestResults | undefined, keys: readonly OrgKeyName[]): boolean {
  return keys.some((k) => keyOk(results, k));
}

function quralystManagedReason(): string {
  // Do not mention API keys the user never enters. Disabled toggles stay silent.
  return '';
}

/** Gate a Quralyst-managed enrichment toggle: enabled while verifying, otherwise needs ≥1 valid key. */
export function quralystManagedGate(
  results: TestResults | undefined,
  keys: readonly OrgKeyName[],
  verifying: boolean,
): ToggleGate {
  if (verifying) return { disabled: false, reason: '' };
  if (anyKeyOk(results, keys)) return { disabled: false, reason: '' };
  return { disabled: true, reason: quralystManagedReason() };
}

/** Include recent news — Brave Search (`news_api_key`). */
export function newsGate(results?: TestResults, verifying = false): ToggleGate {
  return quralystManagedGate(results, ['news_api_key'], verifying);
}

/** FindAll company discovery — Parallel.ai is added by Quralyst, not by the user. */
export function findallGate(results?: TestResults, verifying = false): ToggleGate {
  return quralystManagedGate(results, ['parallel_api_key'], verifying);
}

/** Ownership / acquisition enrichment — any configured LLM or Perplexity. */
export function llmGate(results?: TestResults, verifying = false): ToggleGate {
  return quralystManagedGate(results, LLM_ENRICHMENT_KEYS, verifying);
}

/** Buyer Propensity / acquisition-target / sell-side readiness — Gemini or Perplexity. */
export function webSearchGate(results?: TestResults, verifying = false): ToggleGate {
  return quralystManagedGate(results, READINESS_KEYS, verifying);
}

/** Blank-field web backfill — LLM, Serper, News/Brave, or Perplexity. */
export function blankFieldBackfillGate(results?: TestResults, verifying = false): ToggleGate {
  return quralystManagedGate(results, BLANK_FIELD_KEYS, verifying);
}

/** Shown if Gamma (tearsheet polish) is missing and the user cannot start generation. */
export const GAMMA_NOT_CONFIGURED_MESSAGE = PLATFORM_ADMIN_CONTACT_MESSAGE;

/** Generate-tearsheet actions — Gamma key is Quralyst-managed. */
export function gammaGate(results?: TestResults, verifying = false): ToggleGate {
  if (verifying) return { disabled: false, reason: '' };
  if (keyOk(results, 'gamma_api_key')) return { disabled: false, reason: '' };
  return { disabled: true, reason: GAMMA_NOT_CONFIGURED_MESSAGE };
}

/** Submit-time preflight. Missing Quralyst-managed LLM keys do not block Start.
 *  We still stop a run when the user picked specific models with failover off
 *  and those models are down, or when every configured LLM is rate-limited. */
export function preflightSubmitBlock(
  results: TestResults | undefined,
  verifying: boolean,
  selectedProviders?: LlmProviderName[],
  fallbackEnabled = true,
): string {
  if (verifying) return '';
  if (!results) return '';

  if (selectedProviders && selectedProviders.length > 0 && !fallbackEnabled) {
    const usableSelected = selectedProviders.filter((p) => keyOk(results, LLM_PROVIDER_KEY[p]));
    if (!usableSelected.length) {
      const names = selectedProviders.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(', ');
      return `Cannot start research: none of the selected AI models (${names}) are operational, and fallback is disabled. Enable fallback, or contact Platform Admin.`;
    }
    return '';
  }

  if (anyLlmValid(results)) return '';

  const configured = LLM_KEYS.filter((k) => results[k] && results[k]!.status !== 'not_configured');
  if (configured.length > 0) {
    const rateLimitedOrDown = configured.some((k) =>
      [
        'rate_limited',
        'service_unavailable',
        'credits_exhausted',
        'quota_exceeded',
        'error',
      ].includes(results[k]?.status || ''),
    );
    if (rateLimitedOrDown) {
      return PLATFORM_ADMIN_CONTACT_MESSAGE;
    }
  }

  return '';
}
